import {
  MOVEMENT_TYPE_LABELS, ORDER_STATUS_LABELS, ORDER_TRANSITIONS, PAYMENT_METHOD_LABELS, PAYMENT_STATUS_LABELS, PRICE_SOURCE_LABELS,
  formatDate, formatDateTime, formatILS, type OrderStatus, type PaymentStatus,
} from '@takumi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Banknote, Boxes, Check, Receipt, ShoppingBag } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardHeader, EmptyState, PageLoader } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useInvalidateCrm } from '@/lib/queries';
import type { Order } from '@/lib/sales-types';
import { cn } from '@/lib/utils';
import { InvoiceDialog, PaymentDialog } from '@/features/sales/PaymentDialogs';

export const ORDER_STATUS_VARIANT: Record<OrderStatus, 'neutral' | 'tomorrow' | 'today' | 'matcha' | 'overdue' | 'gold'> = {
  new: 'neutral', confirmed: 'tomorrow', preparing: 'today', shipped: 'gold', delivered: 'matcha', cancelled: 'overdue',
};
export const PAYMENT_STATUS_VARIANT: Record<PaymentStatus, 'overdue' | 'today' | 'matcha' | 'neutral'> = {
  unpaid: 'overdue', partial: 'today', paid: 'matcha', refunded: 'neutral',
};

const FLOW: OrderStatus[] = ['new', 'confirmed', 'preparing', 'shipped', 'delivered'];
const ACTION_LABEL: Partial<Record<OrderStatus, string>> = {
  confirmed: 'אישור הזמנה (מוריד מלאי)', preparing: 'בהכנה', shipped: 'נשלח', delivered: 'נמסר', cancelled: 'ביטול',
};

export function OrderPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const qc = useQueryClient();
  const invalidate = useInvalidateCrm();
  const [paying, setPaying] = useState(false);
  const [invoicing, setInvoicing] = useState(false);
  const { data: o, isLoading } = useQuery({ queryKey: ['order', id], queryFn: () => api.get<Order>(`/orders/${id}`) });
  const status = useMutation({
    mutationFn: (s: OrderStatus) => api.post(`/orders/${id}/status`, { status: s }),
    onSuccess: (_d, s) => {
      void qc.invalidateQueries({ queryKey: ['order', id] });
      void qc.invalidateQueries({ queryKey: ['products'] });
      invalidate(o?.organizationId);
      toast.success(`ההזמנה עודכנה: ${ORDER_STATUS_LABELS[s]}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (isLoading) return <PageLoader />;
  if (!o) return <EmptyState title="ההזמנה לא נמצאה" />;
  const next = ORDER_TRANSITIONS[o.status];
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['order', id] });
    invalidate(o.organizationId);
  };

  return (
    <div className="mx-auto max-w-5xl">
      <Link to={`/org/${o.organizationId}`} className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-ink">
        <ArrowRight className="size-4" /> {o.organizationName}
      </Link>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <h1 className="flex items-center gap-2 text-[26px] font-semibold">
          <ShoppingBag className="size-6 text-gold-500" /> הזמנה <span className="ltr">{o.number}</span>
        </h1>
        <Badge variant={ORDER_STATUS_VARIANT[o.status]}>{ORDER_STATUS_LABELS[o.status]}</Badge>
        <Badge variant={PAYMENT_STATUS_VARIANT[o.paymentStatus]}>{PAYMENT_STATUS_LABELS[o.paymentStatus]}</Badge>
        {o.quoteNumber && <Link to={`/quotes/${o.quoteId}`} className="text-sm text-matcha-700 hover:underline">מהצעה <span className="ltr">{o.quoteNumber}</span></Link>}
      </div>

      {/* Status stepper */}
      {o.status !== 'cancelled' && (
        <ol className="mb-5 flex flex-wrap items-center gap-1 text-sm" aria-label="סטטוס הזמנה">
          {FLOW.map((s, i) => {
            const reached = FLOW.indexOf(o.status) >= i;
            return (
              <li key={s} className="flex items-center gap-1">
                <span className={cn('flex items-center gap-1 rounded-full px-3 py-1 font-semibold', reached ? 'bg-matcha-700 text-white' : 'bg-sunken text-muted')}>
                  {reached && <Check className="size-3.5" />} {ORDER_STATUS_LABELS[s]}
                </span>
                {i < FLOW.length - 1 && <span className="h-px w-5 bg-line-strong" />}
              </li>
            );
          })}
        </ol>
      )}

      {can('sales.write') && (
        <div className="mb-5 flex flex-wrap gap-2">
          {next.filter((s) => s !== 'cancelled').map((s) => (
            <Button key={s} onClick={() => status.mutate(s)} loading={status.isPending}>{ACTION_LABEL[s]}</Button>
          ))}
          {o.status !== 'cancelled' && o.balance > 0 && <Button variant="gold" onClick={() => setPaying(true)}><Banknote /> רישום תשלום</Button>}
          {o.status !== 'cancelled' && <Button variant="secondary" onClick={() => setInvoicing(true)}><Receipt /> רישום חשבונית</Button>}
          {next.includes('cancelled') && (
            <Button variant="ghost" className="text-danger" onClick={() => confirm('לבטל את ההזמנה? מלאי שהורד יוחזר.') && status.mutate('cancelled')}>ביטול הזמנה</Button>
          )}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        <Card className="overflow-hidden">
          <CardHeader title="פריטים" icon={<ShoppingBag />} />
          <table className="w-full text-sm">
            <thead className="bg-sunken/70 text-xs font-bold text-muted">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-start">
                <th>פריט</th><th className="text-end">כמות</th><th className="text-end">מחיר</th><th className="text-end">סה״כ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/70">
              {o.items.map((i) => (
                <tr key={i.id} className="[&>td]:px-3 [&>td]:py-2.5">
                  <td>
                    {i.description}
                    <div className="text-[11px] text-muted">{PRICE_SOURCE_LABELS[i.priceSource]}{i.discountPct ? ` · הנחה ${i.discountPct}%` : ''}</div>
                  </td>
                  <td className="text-end tabular">{i.quantity}</td>
                  <td className="text-end tabular">{formatILS(i.unitPrice)}</td>
                  <td className="text-end font-semibold tabular">{formatILS(i.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <dl className="ms-auto flex w-72 flex-col gap-1 p-4 text-sm tabular">
            <div className="flex justify-between"><dt>סכום ביניים</dt><dd>{formatILS(o.subtotal)}</dd></div>
            {o.discountTotal > 0 && <div className="flex justify-between"><dt>הנחה</dt><dd>-{formatILS(o.discountTotal)}</dd></div>}
            {o.shipping > 0 && <div className="flex justify-between"><dt>משלוח</dt><dd>{formatILS(o.shipping)}</dd></div>}
            <div className="flex justify-between"><dt>מע״מ</dt><dd>{formatILS(o.vatAmount)}</dd></div>
            <div className="mt-1 flex justify-between border-t border-line-strong pt-2 text-base font-bold"><dt>סה״כ</dt><dd>{formatILS(o.total)}</dd></div>
            <div className="flex justify-between text-success"><dt>שולם</dt><dd>{formatILS(o.amountPaid)}</dd></div>
            <div className={cn('flex justify-between font-semibold', o.balance > 0 ? 'text-overdue' : 'text-muted')}><dt>יתרה לתשלום</dt><dd>{formatILS(o.balance)}</dd></div>
          </dl>
        </Card>
        <div className="flex flex-col gap-5">
          <Card>
            <CardHeader title="תשלומים" icon={<Banknote />} count={o.payments.length} />
            {o.payments.length ? (
              <ul className="divide-y divide-line/70 text-sm">
                {o.payments.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 px-4 py-2">
                    <span className="font-semibold tabular">{formatILS(p.amount)}</span>
                    <span className="text-muted">{PAYMENT_METHOD_LABELS[p.method]} · {formatDate(p.paidAt)}</span>
                  </li>
                ))}
              </ul>
            ) : <EmptyState title="לא נרשמו תשלומים" />}
          </Card>
          <Card>
            <CardHeader title="חשבוניות" icon={<Receipt />} count={o.invoices.length} />
            {o.invoices.length ? (
              <ul className="divide-y divide-line/70 text-sm">
                {o.invoices.map((i) => (
                  <li key={i.id} className="flex items-center gap-2 px-4 py-2">
                    <span className="ltr font-semibold">{i.number}</span>
                    <span className="text-muted">{formatDate(i.issueDate)}{i.dueDate && ` · עד ${formatDate(i.dueDate)}`}</span>
                    <Badge variant={i.status === 'paid' ? 'matcha' : 'today'} className="ms-auto">{i.status === 'paid' ? 'שולמה' : 'פתוחה'}</Badge>
                  </li>
                ))}
              </ul>
            ) : <EmptyState title="לא נרשמו חשבוניות" description="חשבונית מס רשמית מופקת בתוכנת הנהלת החשבונות; כאן נשמר הרישום" />}
          </Card>
          <Card>
            <CardHeader title="תנועות מלאי" icon={<Boxes />} />
            {o.movements.length ? (
              <ul className="divide-y divide-line/70 text-sm">
                {o.movements.map((m) => (
                  <li key={m.id} className="flex items-center gap-2 px-4 py-2">
                    <span>{MOVEMENT_TYPE_LABELS[m.movementType]}</span>
                    <span className="text-muted">{m.productName}</span>
                    <span className={cn('ms-auto font-semibold tabular ltr', m.quantity < 0 ? 'text-overdue' : 'text-success')}>{m.quantity > 0 ? '+' : ''}{m.quantity}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="p-4 text-sm text-muted">המלאי יעודכן באישור ההזמנה</p>}
          </Card>
          <p className="text-xs text-muted">נוצרה {formatDateTime(o.createdAt)}{o.confirmedAt && ` · אושרה ${formatDateTime(o.confirmedAt)}`}{o.deliveredAt && ` · נמסרה ${formatDateTime(o.deliveredAt)}`}</p>
        </div>
      </div>
      <PaymentDialog open={paying} onOpenChange={setPaying} organizationId={o.organizationId} orderId={o.id} defaultAmount={o.balance} onDone={refresh} />
      <InvoiceDialog open={invoicing} onOpenChange={setInvoicing} orderId={o.id} onDone={refresh} />
    </div>
  );
}
